import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SEOHead } from "../components/SEOHead";
import { DatePollResults } from "../components/DatePollResults";
import { usePollBySlug } from "@core/hooks/usePoll";
import { PollHeader } from "@core/components/voting/PollHeader";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { supabase } from "@core/lib/supabase";
import { logger } from "@core/lib/logger";
import {
  getGuestIdentity,
  getDeviceId,
  getOwnerTokenForPoll,
  getKeyringTokenForPoll,
  addPollOwnerToken,
  extractOwnerTokenFromUrl,
} from "@core/lib/guestIdentityService";
import {
  GUEST_POLL_CLAIMED_EVENT,
  GUEST_POLL_CLAIM_FAILED_EVENT,
  type GuestPollClaimedDetail,
  type GuestPollClaimFailedDetail,
} from "@core/lib/pendingPollClaim";
import { useToast } from "@core/hooks/use-toast";
import { useAuth } from "@core/contexts/AuthContext";
import { GuestOwnerPanel } from "../components/GuestOwnerPanel";
import {
  GuestAccountNudge,
  shouldShowGuestAccountNudge,
} from "../components/GuestAccountNudge";
import { deletePollResponse } from "@core/lib/edgeFunctionClient";
import { ResultsAccessDenied } from "@core/components/polls/ResultsAccessDenied";
import { PromoSlot } from "../components/PromoSlot";
import { buildPollOptions } from "@core/lib/buildPollOptions";
import { PollTemplateHeader } from "../components/PollTemplateHeader";
import { toIntlLocale } from "@core/lib/date-utils";

/** Libellé d'un créneau pour le récapitulatif de brouillon (date + horaire). */
function formatDraftSlotLabel(
  option: {
    option_date: string;
    date_group_label: string | null;
    time_label: string | null;
  },
  language: string,
): string {
  let dateLabel = option.date_group_label;
  if (!dateLabel) {
    const [year, month, day] = option.option_date.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    dateLabel = Number.isNaN(date.getTime())
      ? option.option_date
      : date.toLocaleDateString(toIntlLocale(language), {
          weekday: "short",
          day: "numeric",
          month: "short",
        });
  }
  return option.time_label ? `${dateLabel} · ${option.time_label}` : dateLabel;
}

/**
 * Page de résultats pour les sondages de dates
 * Migration vers architecture unifiée avec usePollBySlug
 */
/**
 * Métadonnées d'affichage renvoyées par la RPC `get_poll_results_access`.
 * `response_count` est renseigné même quand `can_view` est faux : c'est ce qui
 * permet d'annoncer « 3 personnes ont voté » sans divulguer les résultats.
 */
interface ResultsAccess {
  can_view: boolean;
  visibility: string;
  response_count: number;
  has_voted: boolean;
  is_creator: boolean;
}

/**
 * États d'affichage de la page Résultats. Remplace l'ancien test
 * `responses.length === 0`, qui confondait « aucun vote » et « je n'ai pas le
 * droit de voir les votes » — d'où l'affichage « 0 votant » sur un sondage
 * qui en comptait trois.
 */
type ResultsState =
  | "loading"
  | "empty"
  | "hidden-voters"
  | "hidden-creator"
  | "visible";

function deriveResultsState(access: ResultsAccess | null): ResultsState {
  if (!access) return "loading";
  if (access.can_view) {
    return access.response_count === 0 ? "empty" : "visible";
  }
  return access.visibility === "voters" ? "hidden-voters" : "hidden-creator";
}

/**
 * Jeton reçu par lien d'admin, mémorisé par slug : sous StrictMode,
 * l'initialiseur de `useState` est rejoué alors que l'URL a déjà été
 * nettoyée — sans ce mémo, le second passage ne verrait plus le jeton.
 */
const tokensReceivedByLink = new Map<string, string>();

/**
 * Lien d'admin (e-mail aujourd'hui, IA demain) : lit le jeton dans le
 * fragment `#owner_token=` puis dans la query `?owner_token=` (liens déjà
 * envoyés) et le retire de l'URL : c'est un secret porteur, il ne doit pas
 * rester dans l'historique. Exécuté pendant le premier rendu.
 *
 * Le jeton n'est PAS encore rangé dans le trousseau : il ne l'est qu'une fois
 * confirmé par le serveur (`is_creator`). Sinon un lien piégé ou périmé
 * masquerait le bon jeton du propriétaire — `getOwnerTokenForPoll` lit le
 * trousseau avant le jeton principal — et lui ferait perdre la main sur son
 * propre sondage.
 */
function readOwnerTokenFromUrl(slug: string | undefined): string | null {
  if (!slug || typeof window === "undefined") return null;
  const { token, cleanUrl } = extractOwnerTokenFromUrl(window.location.href);
  if (cleanUrl !== null) {
    window.history.replaceState(window.history.state, "", cleanUrl);
  }
  if (token) {
    tokensReceivedByLink.set(slug, token);
    return token;
  }
  return tokensReceivedByLink.get(slug) ?? null;
}

export default function Results() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const [votes, setVotes] = useState<any[]>([]);
  const [access, setAccess] = useState<ResultsAccess | null>(null);
  const [linkToken] = useState<string | null>(() =>
    readOwnerTokenFromUrl(slug),
  );
  const [statusOverride, setStatusOverride] = useState<string | null>(null);
  // Jeton du lien, seulement une fois reconnu par le serveur : c'est lui que
  // le bandeau « Gardez ce lien » recopie, jamais un jeton refusé.
  const [verifiedLinkToken, setVerifiedLinkToken] = useState<string | null>(
    null,
  );

  // Utiliser le hook unifié avec le slug
  const { data: poll, isLoading, error, refetch } = usePollBySlug(slug);
  const { toast } = useToast();
  // Incrémenté après un rattachement au compte : relance le chargement des
  // droits (is_creator passe par le compte, plus par le jeton).
  const [reloadKey, setReloadKey] = useState(0);

  const handleClaimed = useCallback(() => {
    setVerifiedLinkToken(null);
    void refetch?.();
    setReloadKey((k) => k + 1);
  }, [refetch]);

  // Rattachement explicite demandé avant connexion (« Créer un compte ») :
  // consommé par AuthContext après SIGNED_IN, qui notifie ici.
  useEffect(() => {
    if (!poll?.id) return;
    const onClaimed = (e: Event) => {
      const detail = (e as CustomEvent<GuestPollClaimedDetail>).detail;
      if (detail?.pollId !== poll.id) return;
      toast({ title: t("polls:guest_admin.claimed_toast") });
      handleClaimed();
    };
    const onFailed = (e: Event) => {
      const detail = (e as CustomEvent<GuestPollClaimFailedDetail>).detail;
      if (detail?.pollId !== poll.id) return;
      toast({
        title: t("errors:generic"),
        description: t(
          detail.reason === "rate_limited"
            ? "polls:guest_admin.claim_rate_limited"
            : "polls:guest_admin.claim_error",
        ),
        variant: "destructive",
      });
    };
    window.addEventListener(GUEST_POLL_CLAIMED_EVENT, onClaimed);
    window.addEventListener(GUEST_POLL_CLAIM_FAILED_EVENT, onFailed);
    return () => {
      window.removeEventListener(GUEST_POLL_CLAIMED_EVENT, onClaimed);
      window.removeEventListener(GUEST_POLL_CLAIM_FAILED_EVENT, onFailed);
    };
  }, [poll?.id, handleClaimed, toast, t]);

  const handleDeleteVote = useCallback(
    async (responseId: string) => {
      const identity = await getGuestIdentity();
      // Phase 1 security fix #859: Use Edge Function wrapper instead of direct RPC
      let rpcError;
      try {
        await deletePollResponse({
          response_id: responseId,
          fingerprint: identity.stableFingerprint || undefined,
          // Seule preuve de possession d'un créateur invité : l'empreinte
          // ne suffit plus côté serveur (20260928190000). Jeton DU sondage
          // (trousseau), pas seulement le jeton principal du navigateur.
          owner_token: getOwnerTokenForPoll(poll?.id, slug),
        });
        rpcError = null;
      } catch (err) {
        rpcError = err instanceof Error ? err : new Error(String(err));
      }
      if (rpcError) throw rpcError;
      setVotes((prev) => prev.filter((v) => v.id !== responseId));
    },
    [poll?.id, slug],
  );

  // Charger les votes depuis Supabase quand le poll est disponible
  useEffect(() => {
    if (!poll?.id) return;

    const loadVotes = async () => {
      try {
        // Le fingerprint DOIT être calculé exactement comme pour
        // `get_my_poll_response` (identity.stableFingerprint), sinon la
        // correspondance côté serveur échoue et un votant légitime est traité
        // comme un inconnu → 0 ligne sur un sondage `voters`.
        const identity = await getGuestIdentity();
        const fingerprint = identity.stableFingerprint || null;
        const deviceId = getDeviceId();

        // Jeton reçu par lien : on le vérifie AVANT de le ranger. Il n'entre
        // dans le trousseau (sous l'UUID et le slug) que si le serveur le
        // reconnaît comme propriétaire ; sinon on garde le jeton déjà connu.
        const storedToken = getOwnerTokenForPoll(poll.id, slug);
        const argsFor = (token: string | null) => ({
          p_poll_id: poll.id,
          p_device_id: deviceId,
          p_fingerprint: fingerprint,
          ...(token ? { p_owner_token: token } : {}),
        });

        let ownerToken = storedToken;
        let accessResult = null as Awaited<
          ReturnType<typeof supabase.rpc>
        > | null;
        if (linkToken && linkToken !== storedToken) {
          const linkAccess = await supabase.rpc(
            "get_poll_results_access",
            argsFor(linkToken),
          );
          const linkRow = ((linkAccess.data as ResultsAccess[]) || [])[0];
          if (!linkAccess.error && linkRow?.is_creator) {
            addPollOwnerToken(poll.id, linkToken);
            addPollOwnerToken(slug ?? poll.id, linkToken);
            ownerToken = linkToken;
            accessResult = linkAccess;
            setVerifiedLinkToken(linkToken);
          }
        }

        const rpcArgs = argsFor(ownerToken);

        // RPC SECURITY DEFINER : lockdown RLS anon (bug hunt 2026-04-23).
        const [accessFallback, responsesResult] = await Promise.all([
          accessResult
            ? Promise.resolve(accessResult)
            : supabase.rpc("get_poll_results_access", rpcArgs),
          supabase.rpc("get_poll_responses", rpcArgs),
        ]);
        accessResult = accessFallback;

        const { data: accessRows, error: accessError } = accessResult;
        const { data: rpcData, error: fetchError } = responsesResult;

        if (accessError) {
          logger.error("Erreur chargement accès résultats", "vote", {
            error: accessError,
          });
        }

        const accessRow = ((accessRows as ResultsAccess[]) || [])[0] || null;
        // Lien déjà connu du trousseau (rouvert une 2e fois) : pas de
        // vérification séparée ci-dessus, on le valide ici.
        if (linkToken && ownerToken === linkToken && accessRow?.is_creator) {
          setVerifiedLinkToken(linkToken);
        }

        const data = ((rpcData as any[]) || []).filter(
          (r) => r.response_type === "date",
        );

        // Dégradation sûre : si la RPC de métadonnées échoue, on retombe sur
        // le comportement d'avant (afficher ce que `get_poll_responses` a
        // bien voulu renvoyer). Aucune fuite possible — c'est cette RPC-là qui
        // applique le contrôle d'accès côté serveur. Surtout, `access` n'est
        // jamais laissé à null : sinon la page resterait bloquée sur le
        // spinner.
        setAccess(
          accessRow ?? {
            can_view: true,
            visibility: "public",
            response_count: data.length,
            has_voted: false,
            is_creator: false,
          },
        );

        if (fetchError) {
          logger.error("Erreur chargement votes", "vote", {
            error: fetchError,
          });
          return;
        }

        // Transformer poll_responses en format Vote attendu par DatePollResults
        // Utiliser votingDisplayNameService pour afficher les noms anonymes au lieu des fingerprints
        logger.info("RESULTS_PAGE - Loading votes from Supabase", "vote", {
          pollId: poll.id,
          rawResponsesCount: data?.length || 0,
          canView: accessRow?.can_view ?? null,
          visibility: accessRow?.visibility ?? null,
          serverResponseCount: accessRow?.response_count ?? null,
          hasVoted: accessRow?.has_voted ?? null,
          isCreator: accessRow?.is_creator ?? null,
          hasFingerprint: !!fingerprint,
        });

        // Le voterName est déjà présent dans response_data (posé au moment du
        // vote), pas besoin de refaire une requête par participant.
        const transformedVotes = (data || []).map((response: any) => {
          const rawVoterName = response.response_data?.voterName;
          const displayName =
            typeof rawVoterName === "string" &&
            rawVoterName.trim() &&
            rawVoterName !== "NO_NAME"
              ? rawVoterName.trim()
              : t("polls:results_anonymous");

          return {
            id: response.id,
            poll_id: response.poll_id,
            voter_email: response.respondent_email || "",
            voter_name: displayName,
            selections: Object.fromEntries(
              Object.entries(response.response_data || {}).filter(
                ([k]) => k !== "voterName",
              ),
            ),
            created_at: response.created_at,
          };
        });

        // LOG: Analyser les doublons et comprendre pourquoi 3 votes au lieu de 1
        const votesByRespondent = new Map();
        transformedVotes.forEach((vote) => {
          const key = `${vote.voter_name}_${JSON.stringify(vote.selections)}`;
          if (!votesByRespondent.has(key)) {
            votesByRespondent.set(key, []);
          }
          votesByRespondent.get(key).push(vote);
        });

        logger.info("RESULTS_PAGE - All votes transformed", "vote", {
          pollId: poll.id,
          transformedVotesCount: transformedVotes.length,
          voterNames: transformedVotes.map((v) => v.voter_name),
          voteDetails: transformedVotes.map((v) => ({
            voterName: v.voter_name,
            selectionCount: Object.keys(v.selections).length,
            selections: v.selections,
          })),
          duplicateAnalysis: {
            uniqueCombinations: votesByRespondent.size,
            duplicates: Array.from(votesByRespondent.entries()).map(
              ([key, votes]) => ({
                key,
                count: votes.length,
                voteIds: votes.map((v) => v.id),
                voterName: votes[0].voter_name,
                selections: votes[0].selections,
              }),
            ),
          },
        });

        // Dédupliquer les votes : fusionner toutes les lignes du même répondant
        // (les options des lignes plus récentes écrasent les anciennes — compatibilité avec l'ancien modèle 1 ligne/option)
        const uniqueVotes = new Map<string, any>();
        const sortedByDate = [...transformedVotes].sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
        sortedByDate.forEach((vote) => {
          const key = vote.voter_name;
          if (!uniqueVotes.has(key)) {
            uniqueVotes.set(key, { ...vote });
          } else {
            uniqueVotes.set(key, {
              ...uniqueVotes.get(key),
              selections: {
                ...uniqueVotes.get(key).selections,
                ...vote.selections,
              },
              created_at: vote.created_at,
            });
          }
        });

        const deduplicatedVotes = Array.from(uniqueVotes.values());

        logger.info("RESULTS_PAGE - Deduplication applied", "vote", {
          originalCount: transformedVotes.length,
          deduplicatedCount: deduplicatedVotes.length,
          duplicatesRemoved: transformedVotes.length - deduplicatedVotes.length,
          uniqueVoters: Array.from(uniqueVotes.keys()),
        });

        setVotes(deduplicatedVotes);
        logger.info("Votes chargés depuis Supabase", "vote", {
          count: deduplicatedVotes.length,
          pollId: poll.id,
        });
      } catch (err) {
        logger.error("Erreur chargement votes", "vote", { error: err });
        // Ne jamais laisser `access` à null : la page resterait sur le spinner.
        setAccess(
          (prev) =>
            prev ?? {
              can_view: true,
              visibility: "public",
              response_count: 0,
              has_voted: false,
              is_creator: false,
            },
        );
      }
    };

    loadVotes();
    // `user?.id` : après connexion depuis l'encart « Créer un compte », la
    // propriété est réévaluée (sondage rattaché au compte).
  }, [poll?.id, user?.id, reloadKey]);

  // `isLoading` couvre le chargement du sondage, `!access` celui de ses
  // métadonnées de visibilité. Sans ce second test, la page afficherait
  // « 0 votant » le temps que la RPC réponde.
  if (isLoading || (poll && !access)) {
    return (
      <div className="flex flex-col min-h-screen-safe bg-background">
        <ProductHeader productType="date" showAuth={true} />
        <div className="flex flex-1">
          <ProductSidebar productType="date" />
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !poll) {
    return (
      <div className="flex flex-col min-h-screen-safe bg-background">
        <ProductHeader productType="date" showAuth={true} />
        <div className="flex flex-1">
          <ProductSidebar productType="date" />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-muted-foreground">
                {error
                  ? t("polls:results.load_error")
                  : t("polls:results.poll_not_found")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const resultsState = deriveResultsState(access);

  // CTA de l'écran verrouillé. Deux cas distincts :
  // - hidden-voters : l'appelant n'a PAS voté (sinon il serait reconnu et
  //   can_view serait vrai) → « Voter maintenant ».
  // - hidden-creator (creator-only/restricted) : un votant du sondage reste
  //   verrouillé ; s'il a voté, on lui propose « Modifier mon vote » — sauf
  //   si allowEditAfterSubmit=false (le vote serait refusé par le serveur).
  const allowEdit =
    String(
      (poll.settings as { allowEditAfterSubmit?: unknown })
        ?.allowEditAfterSubmit ?? "",
    ) !== "false";
  const showLockedCta =
    resultsState === "hidden-voters" ||
    (resultsState === "hidden-creator" &&
      (access?.has_voted ?? false) &&
      allowEdit);

  // États verrouillés : on masque TOUT l'écran de résultats (compteur de
  // votants, bandeau « créneau en tête », grille, onglets, bouton Exporter),
  // qui n'afficherait que des zéros trompeurs faute de données.
  if (resultsState === "hidden-voters" || resultsState === "hidden-creator") {
    return (
      <div className="flex flex-col min-h-screen-safe bg-background">
        <SEOHead
          title={t("polls:seo_results_title", { title: poll.title })}
          description={t("polls:seo_results_description")}
          noIndex
        />
        <ProductHeader productType="date" showAuth={true} />
        <div className="flex flex-1">
          <ProductSidebar productType="date" />
          <div className="flex-1">
            <ResultsAccessDenied
              message={
                resultsState === "hidden-voters"
                  ? t("common:results_access.must_vote")
                  : t("common:results_access.creator_only")
              }
              pollSlug={poll.slug || slug}
              voteHref={`/poll/${poll.slug || slug}/vote`}
              showVoteButton={showLockedCta}
              hasVoted={access?.has_voted ?? false}
              responseCount={access?.response_count}
            />
          </div>
        </div>
      </div>
    );
  }

  // Source unique avec Voting : une option par créneau horaire activé.
  // IDs alignés avec VotingSwipe ("1", "2", ...) — cf. buildPollOptions.ts.
  const pollOptions = buildPollOptions(poll).map((opt) => ({
    ...opt,
    poll_id: poll.id,
    votes_count: 0,
  }));

  // Adapter le poll pour PollHeader (description et creator_id requis)
  const adaptedPoll = {
    ...poll,
    status: (statusOverride ?? poll.status) as typeof poll.status,
    description: poll.description || "",
    expires_at: poll.expires_at || "",
    creator_id: poll.creator_id || "unknown",
  };

  // Calculer le nombre de votants uniques
  const totalVoters =
    votes.length > 0
      ? new Set(votes.map((v) => v.voter_email || v.voter_name).filter(Boolean))
          .size
      : 0;

  const template = poll.template ?? null;

  // Panneau d'admin invité :
  // - invité reconnu par son jeton → panneau + encart « Créer un compte » ;
  // - connecté, sondage à SON compte → rien ici (menu propriétaire existant) ;
  // - connecté, sondage INVITÉ dont le trousseau détient un jeton vérifié
  //   (lien d'admin reçu) → panneau + bouton « Rattacher à mon compte ».
  //   Jamais de rattachement implicite (bug du 28/09).
  const ownedByAccount =
    !!user && !!poll.creator_id && poll.creator_id === user.id;
  const claimToken =
    user &&
    !ownedByAccount &&
    (poll as { creator_type?: string }).creator_type !== "user"
      ? getKeyringTokenForPoll(poll.id, slug)
      : null;
  const showGuestOwnerPanel = !!access?.is_creator && (!user || !!claimToken);

  return (
    <div className="flex flex-col min-h-screen-safe bg-background">
      <SEOHead
        title={t("polls:seo_results_title", { title: poll.title })}
        description={t("polls:seo_results_description")}
        noIndex
      />
      <ProductHeader productType="date" showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType="date" />
        <div
          className="flex-1 flex flex-col min-w-0"
          style={
            template
              ? { backgroundColor: `${template.secondary_color}1a` }
              : undefined
          }
        >
          {template && (
            <PollTemplateHeader template={template} title={poll.title} />
          )}
          <div className="flex-1 min-w-0">
            <DatePollResults
              poll={adaptedPoll}
              options={pollOptions}
              votes={votes}
              totalVoters={totalVoters}
              onBack={() => window.history.back()}
              onDeleteVote={handleDeleteVote}
              isCreator={access?.is_creator ?? false}
              ownedByAccount={ownedByAccount}
              onFinalDateFixed={() => setStatusOverride("closed")}
              topBanner={
                showGuestOwnerPanel &&
                shouldShowGuestAccountNudge(
                  access?.response_count ?? 0,
                  claimToken,
                ) ? (
                  <GuestAccountNudge
                    poll={{ id: poll.id, slug: poll.slug || slug || "" }}
                    responseCount={access?.response_count ?? 0}
                  />
                ) : undefined
              }
              ownerPanel={
                showGuestOwnerPanel ? (
                  <GuestOwnerPanel
                    poll={{
                      id: poll.id,
                      slug: poll.slug || slug || "",
                      title: poll.title,
                      status: adaptedPoll.status,
                      description: poll.description,
                    }}
                    draftSlots={
                      adaptedPoll.status === "draft"
                        ? [...pollOptions]
                            .sort((a, b) =>
                              String(a.option_date || "").localeCompare(
                                String(b.option_date || ""),
                              ),
                            )
                            .map((opt) => ({
                              id: opt.id,
                              label: formatDraftSlotLabel(opt, i18n.language),
                            }))
                        : undefined
                    }
                    adminTokenFromLink={verifiedLinkToken}
                    onStatusChange={setStatusOverride}
                    claimToken={claimToken}
                    onClaimed={handleClaimed}
                    responseCount={access?.response_count ?? 0}
                  />
                ) : undefined
              }
            />
          </div>
          {/* Collé en bas sur mobile : sinon l'encart (offre d'essai
              comprise) n'apparaît qu'après avoir scrollé tous les résultats. */}
          <div className="sticky bottom-0 z-10 pb-[env(safe-area-inset-bottom)] bg-background md:static md:pb-0">
            <PromoSlot
              placement="results_page"
              isCreator={access?.is_creator ?? false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
